import React, { useMemo } from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme';
import { Chip, Heading, Text, InfoRoundIconSmall } from '@weave/design-system';
import {
  findPmsCapabilities,
  PMS_CAPABILITY_KEYS,
  PMS_CAPABILITY_LABELS,
} from '../../constants/pms-capabilities';

type PmsCapabilitiesChipsProps = {
  pmsName?: string;
  isLoading?: boolean;
};

export const PmsCapabilitiesChips = ({
  pmsName,
  isLoading,
}: PmsCapabilitiesChipsProps) => {
  const pmsCapability = useMemo(
    () => (pmsName ? findPmsCapabilities(pmsName) : undefined),
    [pmsName]
  );

  if (isLoading || !pmsName) {
    return null;
  }

  const supportedCapabilities = pmsCapability
    ? PMS_CAPABILITY_KEYS.filter((key) => pmsCapability[key])
    : [];

  const unsupportedCapabilities = pmsCapability
    ? PMS_CAPABILITY_KEYS.filter((key) => !pmsCapability[key])
    : [];

  return (
    <section css={containerStyles}>
      <Heading level={3} css={headingStyles}>
        PMS Capabilities
      </Heading>
      <Text size="medium" color="subdued" css={pmsNameStyles}>
        Practice Management System:
        <Text
          css={{ marginLeft: theme.spacing(0.5) }}
          as="span"
          size="medium"
          weight="bold"
        >
          {pmsCapability?.practiceManagementSystem ?? pmsName}
        </Text>
      </Text>

      {!pmsCapability && (
        <Text size="medium" color="subdued" css={noMatchStyles}>
          PMS capabilities data is not available for "{pmsName}".
        </Text>
      )}

      {pmsCapability && (
        <div css={chipsContainerStyles}>
          {supportedCapabilities.map((key) => (
            <Chip key={key} css={chipStyles}>
              {PMS_CAPABILITY_LABELS[key]}
            </Chip>
          ))}
          {unsupportedCapabilities.map((key) => (
            <Chip key={key} variant="disabled" css={disabledChipStyles}>
              {PMS_CAPABILITY_LABELS[key]}
            </Chip>
          ))}
        </div>
      )}

      {pmsCapability?.notes && (
        <div css={notesStyles}>
          <InfoRoundIconSmall />
          <Text size="small" color="subdued">
            {pmsCapability.notes}
          </Text>
        </div>
      )}
    </section>
  );
};

const containerStyles = css({
  borderRadius: theme.borderRadius.medium,
  border: `1px solid ${theme.colors.neutral50}`,
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
  backgroundColor: theme.colors.white,
});

const headingStyles = css({
  margin: 0,
  marginBottom: theme.spacing(0.5),
});

const pmsNameStyles = css({
  marginBottom: theme.spacing(2),
});

const noMatchStyles = css({
  fontStyle: 'italic',
});

const chipsContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  width: '100%',
});

const chipStyles = css({
  '&&': {
    maxWidth: 'none',
    width: 'fit-content',
    whiteSpace: 'nowrap',
    cursor: 'default',
    color: theme.colors.black,
    backgroundColor: theme.colors.success20,
  },
});

const disabledChipStyles = css({
  '&&': {
    maxWidth: 'none',
    width: 'fit-content',
    whiteSpace: 'nowrap',
    cursor: 'default',
    opacity: 0.5,
    textDecoration: 'line-through',
  },
});

const notesStyles = css({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: theme.borderRadius.small,
  backgroundColor: theme.colors.warning5,
});
